import dayjs from 'dayjs';
import type { ApplicantMessage, CommunicationMedium } from '../services/types/applicant.types';

const COMMUNICATION_MEDIUMS: CommunicationMedium[] = ['WHATSAPP', 'EMAIL', 'ZALO'];

export const formatTime = (sentTime: string): string => {
  try {
    const parts = sentTime.split(':');
    if (parts.length < 2) return sentTime;
    const hour = parseInt(parts[0], 10);
    const minutes = parts[1];
    if (isNaN(hour) || !minutes) return sentTime;
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}.${minutes} ${ampm}`;
  } catch {
    return sentTime;
  }
};

export const isWithinCurrentWeek = (sendDate: string): boolean => {
  try {
    const messageDate = dayjs(sendDate).startOf('day');
    const today = dayjs().startOf('day');
    const sevenDaysAgo = dayjs().subtract(7, 'days').startOf('day');

    return (
      messageDate.isSame(today) ||
      (messageDate.isAfter(sevenDaysAgo) && messageDate.isBefore(today))
    );
  } catch {
    return false;
  }
};

export const formatDateSeparator = (sendDate: string): string => {
  try {
    const messageDate = dayjs(sendDate);
    if (!messageDate.isValid()) return sendDate;
    const today = dayjs().startOf('day');
    const yesterday = dayjs().subtract(1, 'day').startOf('day');
    const messageDateStart = messageDate.startOf('day');

    if (messageDateStart.isSame(today)) {
      return 'Today';
    }
    if (messageDateStart.isSame(yesterday)) {
      return 'Yesterday';
    }
    if (isWithinCurrentWeek(sendDate)) {
      return messageDate.format('dddd');
    }
    return messageDate.format('dddd, MMMM DD');
  } catch {
    return sendDate;
  }
};

export const formatMessageDateTime = (
  sendDate: string,
  sentTime: string,
  isWithinWeek: boolean,
): string => {
  try {
    if (isWithinWeek) {
      const messageDate = dayjs(sendDate);
      const today = dayjs().startOf('day');
      const yesterday = dayjs().subtract(1, 'day').startOf('day');
      const messageDateStart = messageDate.startOf('day');

      let dayLabel = messageDate.format('dddd');
      if (messageDateStart.isSame(today)) {
        dayLabel = 'Today';
      } else if (messageDateStart.isSame(yesterday)) {
        dayLabel = 'Yesterday';
      }

      return `${dayLabel} ${formatTime(sentTime)}`;
    }
    const messageDate = dayjs(sendDate);
    return `${messageDate.format('MM/DD')} ${formatTime(sentTime)}`;
  } catch {
    return formatTime(sentTime);
  }
};

export const groupMessagesByDate = (
  messages: ApplicantMessage[],
): Record<string, ApplicantMessage[]> => {
  const groups: Record<string, ApplicantMessage[]> = {};

  messages.forEach((message) => {
    const dateKey = message.send_date.split('T')[0];
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(message);
  });

  return groups;
};

export const groupMessagesByMedium = (
  messages: ApplicantMessage[],
): Record<CommunicationMedium, ApplicantMessage[]> => {
  const groups: Record<CommunicationMedium, ApplicantMessage[]> = {
    WHATSAPP: [],
    EMAIL: [],
    ZALO: [],
  };

  messages.forEach((message) => {
    const medium = message.communication_medium;
    if (COMMUNICATION_MEDIUMS.includes(medium)) {
      groups[medium].push(message);
    }
  });

  return groups;
};

/** Hour:minute key for burst grouping (UI shows minute precision, not seconds). */
export const getSentTimeMinuteKey = (sentTime: string): string => {
  const parts = sentTime.split(':');
  if (parts.length < 2) return sentTime;
  return `${parts[0]}:${parts[1]}`;
};

export const isSameMessageBurst = (
  prev: ApplicantMessage | null,
  curr: ApplicantMessage,
): boolean =>
  !!prev &&
  prev.sender_type === curr.sender_type &&
  getSentTimeMinuteKey(prev.sent_time) === getSentTimeMinuteKey(curr.sent_time);

/** True when curr is the last message in a same-sender, same-minute run. */
export const isLastInMessageBurst = (
  curr: ApplicantMessage,
  next: ApplicantMessage | null,
): boolean => !next || !isSameMessageBurst(curr, next);

export const sortMessagesByDateTime = (
  messages: ApplicantMessage[],
): ApplicantMessage[] =>
  [...messages].sort((a, b) => {
    const dateA = `${a.send_date.split('T')[0]}T${a.sent_time}`;
    const dateB = `${b.send_date.split('T')[0]}T${b.sent_time}`;
    if (dateA !== dateB) {
      return dateA < dateB ? -1 : 1;
    }
    return a.id - b.id;
  });

export const getInitials = (name: string): string => {
  const trimmed = name?.trim();
  if (!trimmed) {
    return '?';
  }
  const parts = trimmed.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  return trimmed.charAt(0).toUpperCase();
};

export const getFilenameFromUrl = (url: string): string => {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/');
    const filename = segments[segments.length - 1];
    return filename ? decodeURIComponent(filename) : 'Download Document';
  } catch {
    return 'Download Document';
  }
};
